import lodash from 'lodash';
import {CSSProperties, useCallback, useEffect, useMemo, useState} from 'react';

import useScroll from '../useScroll';
import useWindowSize from '../useWindowSize';
import setLayoutTypeAndStyle from './calculation';
import {
    LayoutConditional,
    LayoutMethod,
    LayoutType,
    UseComponentPositionProps,
    UseComponentPositionReturnType,
} from './types';
import {applyStyleOffset, getInitialLayoutType} from './utils';

/**
 * Custom hook that returns a layout type and style object based on the position of the provided element
 */
const useComponentPosition = <L extends LayoutMethod>({
    boundaryElement,
    containerInner,
    containerOuter,
    element,
    layoutMethod,
    layoutOffset = 0,
    matchWidth,
}: UseComponentPositionProps<L>): UseComponentPositionReturnType<L> => {
    const [width] = useWindowSize();
    const {y} = useScroll();
    const [style, setStyle] = useState<CSSProperties>({left: 0, top: 0});
    const [layoutType, setLayoutType] = useState<LayoutType>(() =>
        getInitialLayoutType(layoutMethod),
    );

    /**
     * Helper function to set the coordinates of the element
     * if the provided coordinates are different from the current ones
     */
    const setStyleIfDifferent = useCallback(
        (newCoordinates: CSSProperties) => {
            if (!lodash.isEqual(style, newCoordinates))
                setStyle(newCoordinates);
        },
        [style],
    );

    useEffect(() => {
        const {layoutType: newLayoutType, ...newStyle} =
            setLayoutTypeAndStyle({
                boundaryElement,
                containerInner,
                containerOuter,
                element,
                layoutMethod,
                matchWidth,
            }) || {};

        if (newLayoutType) {
            setLayoutType(newLayoutType);
            setStyleIfDifferent(newStyle);
        }
    }, [
        boundaryElement,
        containerInner,
        containerOuter,
        element,
        layoutMethod,
        layoutOffset,
        layoutType,
        matchWidth,
        setStyleIfDifferent,
        width,
        y,
    ]);

    return useMemo(
        () => ({
            layout: layoutType as LayoutConditional<L>,
            style: applyStyleOffset(
                style,
                layoutType as LayoutType,
                layoutOffset,
            ),
        }),
        [element, layoutOffset, layoutType, matchWidth, style, width],
    );
};

export default useComponentPosition;
